import React, { CSSProperties, useEffect } from 'react';
import { st, classes } from './CustomModalLayout.st.css';
import BaseModalLayout from '../BaseModalLayout';
import deprecationLog from '../utils/deprecationLog';
import type { CustomModalLayoutProps } from './CustomModalLayout.types';

const CustomModalLayout = ({
  theme = 'standard', // TODO: add default value for skin once theme is removed
  actionsSize = 'small',
  removeContentPadding = false,
  showHeaderDivider = 'auto',
  showFooterDivider = 'auto',
  overflowY = 'auto',
  style = {},
  ...propsWithNoDefaults
}: CustomModalLayoutProps) => {
  const {
    children,
    width,
    maxWidth,
    height,
    maxHeight,
    className,
    footnoteSkin,
    ...restProps
  } = {
    theme,
    actionsSize,
    ...propsWithNoDefaults,
  };
  const skin = restProps.skin || theme;

  useEffect(() => {
    if (theme) {
      deprecationLog(
        '<CustomModalLayout/> - prop "theme" is deprecated and will be removed in next major release, please use "skin" property instead.',
      );
    }
    if (overflowY === 'none') {
      deprecationLog(
        '<CustomModalLayout/> - prop "overflowY" value "none" is deprecated and will be removed in next major release, please use "visible" value instead.',
      );
    }
  }, [theme, overflowY]);

  return (
    <BaseModalLayout
      {...restProps}
      skin={skin}
      className={st(
        classes.root,
        {
          removeContentPadding,
          showHeaderDivider: showHeaderDivider === true,
          showFooterDivider: showFooterDivider === true,
        },
        className,
      )}
      style={{
        ...style,
        width: width !== undefined ? width : style.width,
        maxWidth: maxWidth !== undefined ? maxWidth : style.maxWidth,
        height: height !== undefined ? height : style.height,
        maxHeight: maxHeight !== undefined ? maxHeight : style.maxHeight,
      }}
      data-contentpadding={!removeContentPadding}
    >
      <BaseModalLayout.Header
        showHeaderDivider={showHeaderDivider === true}
        titleSize="medium"
        titleTag="h2"
      />
      <BaseModalLayout.Content
        // TODO: remove type cast with major, once the 'none' value is removed
        overflowY={overflowY as CSSProperties['overflowY']}
        hideTopScrollDivider={showHeaderDivider !== 'auto'}
        hideBottomScrollDivider={showFooterDivider !== 'auto'}
      >
        {children}
      </BaseModalLayout.Content>
      <BaseModalLayout.Footer showFooterDivider={showFooterDivider === true} />
      <BaseModalLayout.Footnote skin={footnoteSkin} />
    </BaseModalLayout>
  );
};

CustomModalLayout.Title = BaseModalLayout.Header.Title;

CustomModalLayout.displayName = 'CustomModalLayout';

export default CustomModalLayout;
